/**
 * @file GemFM App - Feedback Dialog.
 * @copyright Copyright (c) 2026 Woolly Bear Software LLC,
 *            all rights reserved.
 * @overview Custom-styled feedback form that posts straight to the Google
 *     Form's own submission endpoint via a hidden same-named iframe target,
 *     rather than iframing the Form's own (unstylable) UI.
 */
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import React from "react";

import * as constants from "../constants";

export interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Feedback dialog component.
 */
function FeedbackDialog({ open, onClose }: FeedbackDialogProps) {
  const targetName = `feedback-target-${React.useId()}`;
  const formRef = React.useRef<HTMLFormElement>(null);
  const awaitingResultRef = React.useRef(false);

  const [email, setEmail] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setEmail("");
      setMessage("");
      setIsSubmitting(false);
      setSubmitted(false);
      setAttemptedSubmit(false);
      awaitingResultRef.current = false;
    }
  }, [open]);

  const messageInvalid = message.trim() === "";
  const showMessageError = attemptedSubmit && messageInvalid;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (messageInvalid) {
      e.preventDefault();
      setAttemptedSubmit(true);
      return;
    }
    awaitingResultRef.current = true;
    setIsSubmitting(true);
    // No preventDefault - the browser's native submission does the POST,
    // targeted at the hidden iframe below.
  };

  const handleTargetLoad = () => {
    if (!awaitingResultRef.current) {
      return;
    }
    awaitingResultRef.current = false;
    setIsSubmitting(false);
    setSubmitted(true);
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Feedback
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ paddingTop: "20px !important" }}>
        {submitted ? (
          <Alert severity="success">Thanks for the feedback!</Alert>
        ) : (
          <form
            ref={formRef}
            action={constants.FEEDBACK_FORM_ACTION_URL}
            method="POST"
            target={targetName}
            onSubmit={handleSubmit}
          >
            <TextField
              name={constants.FEEDBACK_FORM_EMAIL_FIELD}
              label="Email (optional)"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              name={constants.FEEDBACK_FORM_MESSAGE_FIELD}
              label="Your feedback"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isSubmitting}
              error={showMessageError}
              helperText={
                showMessageError
                  ? "Let us know what's on your mind."
                  : undefined
              }
              required
              multiline
              minRows={4}
              fullWidth
              autoFocus
            />
          </form>
        )}
        <iframe
          name={targetName}
          title="feedback-submit-target"
          onLoad={handleTargetLoad}
          style={{ display: "none" }}
        />
      </DialogContent>
      <DialogActions>
        {submitted ? (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button
              variant="outlined"
              color="inherit"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => formRef.current?.requestSubmit()}
              disabled={isSubmitting || messageInvalid}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
            >
              Send
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export { FeedbackDialog };
